import React, { useEffect, useState } from 'react';
import { Modal, Switch, Typography, Button, Space, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { updateAnalyticsConsent } from '../services/AnalyticsService';

const { Text, Paragraph } = Typography;

const STORAGE_KEY = 'heavyDutyMachinery_cookie_consent';

export interface ICookieConsent {
  necessary: true;
  analytics: boolean;
  decidedAt: string;
}

export const getCookieConsent = (): ICookieConsent | undefined => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ICookieConsent) : undefined;
  } catch {
    return undefined;
  }
};

export const setCookieConsent = (analytics: boolean): void => {
  const consent: ICookieConsent = { necessary: true, analytics, decidedAt: new Date().toISOString() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  updateAnalyticsConsent(analytics);
};

interface CookiePreferencesModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * "Configuración > Privacidad > Preferencias de cookies" -- also reachable
 * from the first-visit banner's "Personalizar" button (see
 * CookieConsentBanner below). This toggle actually gates Google Analytics
 * and Microsoft Clarity (see AnalyticsService.ts) -- neither loads until
 * analytics is true here.
 */
export const CookiePreferencesModal: React.FC<CookiePreferencesModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [analytics, setAnalytics] = useState(getCookieConsent()?.analytics ?? false);

  useEffect(() => {
    if (open) setAnalytics(getCookieConsent()?.analytics ?? false);
  }, [open]);

  const handleSave = () => {
    setCookieConsent(analytics);
    onClose();
  };

  return (
    <Modal
      title={translate(t, TRANSLATIONS.SETTINGS_COOKIES_MODAL_TITLE)}
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText={translate(t, TRANSLATIONS.SETTINGS_COOKIES_SAVE_BUTTON)}
      // Same rounded-card look as the rest of the app's modals (see
      // FiltersModal) instead of antd's default square corners/edges.
      styles={{
        content: {
          borderRadius: 20,
          border: '0.5px solid rgba(0,0,0,0.08)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        },
      }}
      okButtonProps={{ shape: 'round' }}
      cancelButtonProps={{ shape: 'round' }}
    >
      <Paragraph type="secondary">{translate(t, TRANSLATIONS.SETTINGS_COOKIES_MODAL_INTRO)}</Paragraph>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
        <div>
          <Text strong>{translate(t, TRANSLATIONS.SETTINGS_COOKIES_NECESSARY_TITLE)}</Text>
          <div>
            <Text type="secondary">{translate(t, TRANSLATIONS.SETTINGS_COOKIES_NECESSARY_DESCRIPTION)}</Text>
          </div>
        </div>
        <Switch checked disabled />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
        <div>
          <Text strong>{translate(t, TRANSLATIONS.SETTINGS_COOKIES_ANALYTICS_TITLE)}</Text>
          <div>
            <Text type="secondary">{translate(t, TRANSLATIONS.SETTINGS_COOKIES_ANALYTICS_DESCRIPTION)}</Text>
          </div>
        </div>
        <Switch checked={analytics} onChange={setAnalytics} />
      </div>
    </Modal>
  );
};

/** First-visit banner -- only renders once, before any consent decision exists in localStorage. */
const CookieConsentBanner: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);

  useEffect(() => {
    setVisible(!getCookieConsent());
  }, []);

  if (!visible) return null;

  return (
    <>
      <div
        style={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          right: 16,
          zIndex: 1100,
          maxWidth: 640,
          margin: '0 auto',
          background: token.colorBgElevated,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: 20,
          boxShadow: token.boxShadowSecondary,
          padding: 20,
        }}
      >
        <Paragraph style={{ marginBottom: 12 }}>{translate(t, TRANSLATIONS.SETTINGS_COOKIES_BANNER_TEXT)}</Paragraph>
        <Space wrap>
          <Button
            type="primary"
            shape="round"
            onClick={() => {
              setCookieConsent(true);
              setVisible(false);
            }}
          >
            {translate(t, TRANSLATIONS.SETTINGS_COOKIES_ACCEPT_ALL_BUTTON)}
          </Button>
          <Button
            shape="round"
            onClick={() => {
              setCookieConsent(false);
              setVisible(false);
            }}
          >
            {translate(t, TRANSLATIONS.SETTINGS_COOKIES_NECESSARY_ONLY_BUTTON)}
          </Button>
          <Button type="link" onClick={() => setCustomizing(true)}>
            {translate(t, TRANSLATIONS.SETTINGS_COOKIES_CUSTOMIZE_BUTTON)}
          </Button>
        </Space>
      </div>
      <CookiePreferencesModal
        open={customizing}
        onClose={() => {
          setCustomizing(false);
          setVisible(false);
        }}
      />
    </>
  );
};

export default CookieConsentBanner;
